import React, { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { FormValues, Forgotpasswordprops } from './Forgotpassword.types';
import Forgotpassworform from './Forgotpasswordform';
import Form from '../../../components/Form/Form';
import Spinner from '../../../components/Spinner/Spinner';
import { forgotPasswordService } from '../../../services/Auth/Auth';
import {
  verifyTotpService,
  verifyRecoveryCodeService,
  verifyAdminTicketService,
} from '../../../services/Recovery/Recovery';
import { Changepasswordcontext } from '../../../context/Changepassword/Changepassword';

type Method = 'totp' | 'code' | 'ticket' | 'email';
type Step = 1 | 2 | 3;

// Flip these back to true and redeploy - no screen redesign needed - once
// the provider they depend on is actually usable again: SendGrid has no
// credits (email), and nobody is configured in ACCOUNT_RECOVERY_OPERATORS
// yet (admin-assisted), so both would only ever fail right now.
const EMAIL_RECOVERY_AVAILABLE = false;
const ADMIN_RECOVERY_AVAILABLE = false;

const METHOD_OPTIONS: {
  id: Method;
  label: string;
  description: string;
  available: boolean;
  unavailableReason?: string;
}[] = [
  {
    id: 'totp',
    label: 'Microsoft Authenticator',
    description: 'Usa el código de 6 dígitos de tu app de autenticación.',
    available: true,
  },
  {
    id: 'code',
    label: 'Código de recuperación',
    description: 'Código vigente de Microsoft Authenticator.',
    available: true,
  },
  {
    id: 'ticket',
    label: 'Recuperación asistida',
    description: 'Un operador autorizado del LIS te entrega un código de un solo uso.',
    available: ADMIN_RECOVERY_AVAILABLE,
    unavailableReason: 'Aún no disponible',
  },
  {
    id: 'email',
    label: 'Correo',
    description: 'Recibe un enlace de recuperación en tu correo institucional.',
    available: EMAIL_RECOVERY_AVAILABLE,
    unavailableReason: 'Temporalmente no disponible',
  },
];

const METHOD_COPY: Record<'totp' | 'code' | 'ticket', { title: string; label: string; placeholder: string; hint: string }> = {
  totp: {
    title: 'Código de Microsoft Authenticator',
    label: 'Código vigente de Microsoft Authenticator',
    placeholder: '123456',
    hint: 'Abre Microsoft Authenticator y escribe el código vigente. No se envía por correo.',
  },
  code: {
    title: 'Código de recuperación',
    label: 'Código de recuperación',
    placeholder: 'XXXX-XXXX-XXXX',
    hint: 'Uno de los códigos de recuperación de Microsoft Authenticator.',
  },
  ticket: {
    title: 'Recuperación asistida',
    label: 'Código entregado por el operador',
    placeholder: 'XXXXXX-XXXXXX',
    hint: 'Un operador autorizado del LIS verifica tu identidad y te entrega este código.',
  },
};

const VERIFY_SERVICE = {
  totp: verifyTotpService,
  code: verifyRecoveryCodeService,
  ticket: verifyAdminTicketService,
};

const Forgotpassword = ({ showModalSuccess, showModalError }: Forgotpasswordprops) => {
  const { register, handleSubmit, formState } = useForm<FormValues>();
  const { updateContext } = React.useContext(Changepasswordcontext);
  const history = useHistory();

  const [step, setStep] = React.useState<Step>(1);
  const [identifier, setIdentifier] = React.useState('');
  const [method, setMethod] = React.useState<Method | null>(null);
  const [secret, setSecret] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const goToStep2 = React.useCallback(() => {
    if (!identifier.trim()) {
      showModalError('Escribe tu usuario institucional para continuar.');
      return;
    }
    setStep(2);
  }, [identifier, showModalError]);

  const chooseMethod = React.useCallback((option: (typeof METHOD_OPTIONS)[number]) => {
    if (!option.available) return;
    setMethod(option.id);
    setSecret('');
    setStep(3);
  }, []);

  const backToStep1 = React.useCallback(() => {
    setStep(1);
    setMethod(null);
    setSecret('');
  }, []);

  const backToStep2 = React.useCallback(() => {
    setStep(2);
    setSecret('');
  }, []);

  const forgotPasswordRequest = React.useCallback(
    async ({ email }: FormValues) => {
      try {
        await forgotPasswordService(email);
        showModalSuccess();
      } catch (error) {
        if (error instanceof Error) showModalError(error.message);
      }
    },
    [showModalSuccess, showModalError]
  );

  const verifyRecovery = React.useCallback(async () => {
    if (!method || method === 'email') return;
    if (!secret.trim()) {
      showModalError('Ingresa el código antes de continuar.');
      return;
    }
    setSubmitting(true);
    try {
      const { resetToken } = await VERIFY_SERVICE[method](identifier, secret);
      updateContext(identifier, resetToken);
      history.push('/changepassword');
    } catch (error) {
      if (error instanceof Error) showModalError(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [identifier, method, secret, updateContext, history, showModalError]);

  const onSubmit = React.useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (step === 1) {
        goToStep2();
      } else if (step === 3 && method === 'email') {
        handleSubmit(forgotPasswordRequest)();
      } else if (step === 3) {
        verifyRecovery();
      }
    },
    [step, method, goToStep2, handleSubmit, forgotPasswordRequest, verifyRecovery]
  );

  return (
    <Form form={{ title: 'Recuperar contraseña', description: 'Elige el método de recuperación que tengas disponible.', onSubmit }}>
      <React.Fragment>
        {step === 1 && (
          <React.Fragment>
            <label className="form-label" htmlFor="recovery-identifier">Usuario institucional</label>
            <input
              className="form-input"
              id="recovery-identifier"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <button className="form-button" type="submit">Continuar</button>
          </React.Fragment>
        )}

        {step === 2 && (
          <React.Fragment>
            <p className="form-label">¿Cómo quieres verificar tu identidad?</p>
            <div className="recovery-method-list">
              {METHOD_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`recovery-method-option${option.available ? '' : ' recovery-method-option-disabled'}`}
                  disabled={!option.available}
                  onClick={() => chooseMethod(option)}
                >
                  <span className="recovery-method-option-label">
                    {option.label}
                    {!option.available && <span className="recovery-method-badge">{option.unavailableReason}</span>}
                  </span>
                  <span className="recovery-method-option-desc">{option.description}</span>
                </button>
              ))}
            </div>
            <button type="button" className="form-link" onClick={backToStep1}>&larr; Volver</button>
          </React.Fragment>
        )}

        {step === 3 && method && method !== 'email' && (
          <React.Fragment>
            <label className="form-label" htmlFor="recovery-secret">{METHOD_COPY[method].label}</label>
            <input
              className="form-input"
              id="recovery-secret"
              autoComplete="one-time-code"
              autoFocus
              placeholder={METHOD_COPY[method].placeholder}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
            <p className="form-support">{METHOD_COPY[method].hint}</p>
            <button className="form-button" type="submit" disabled={submitting} aria-busy={submitting}>
              {submitting ? <><Spinner label="Verificando" /><span>Verificando…</span></> : 'Verificar'}
            </button>
            <button type="button" className="form-link" onClick={backToStep2}>&larr; Volver</button>
          </React.Fragment>
        )}

        {step === 3 && method === 'email' && (
          <React.Fragment>
            <Forgotpassworform register={register} formState={formState} />
            <button type="button" className="form-link" onClick={backToStep2}>&larr; Volver</button>
          </React.Fragment>
        )}
      </React.Fragment>
    </Form>
  );
};

export default Forgotpassword;
